import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { User, Menu, Settings, LogOut } from 'lucide-react';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import { TopBar } from '@/components/chrome/TopBar';
import { cn } from '@/lib/utils';

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', href: '/', public: true },
  ].filter((item) => item.public || isAuthenticated);

  const navLinkClass = (href: string) =>
    cn(
      'px-3 py-1.5 rounded-md text-body-sm font-medium transition-colors',
      location === href
        ? 'bg-brand-50 text-brand-600'
        : 'text-soft hover:text-ink hover:bg-surface-2',
    );

  return (
    <div className="min-h-screen bg-bg-public flex flex-col">
      <TopBar
        leading={
          <div className="flex items-center gap-4">
            <Link href="/" className="flex items-center">
              <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="h-10" />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <Link key={item.name} href={item.href} className={navLinkClass(item.href)}>
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        }
        trailing={
          <div className="flex items-center gap-2">
            <LanguagePill />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.firstName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  {(user?.role === 'admin' || user?.role === 'staff') && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href={user.role === 'admin' ? '/admin/dashboard' : '/dashboard'}>
                          <Settings className="mr-2 h-4 w-4" />
                          {t('common:navigation.dashboard')}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link href="/login">
                  <Button variant="ghost" size="sm">Sign in</Button>
                </Link>
                <Link href="/register">
                  <Button size="sm">Get Started</Button>
                </Link>
              </div>
            )}
            <IconButton
              icon={<Menu />}
              aria-label="Open menu"
              variant="subtle"
              size="md"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden"
            />
          </div>
        }
      />

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <nav className="flex flex-col p-4 gap-1 pt-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={navLinkClass(item.href)}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {!isAuthenticated && (
              <>
                <hr className="my-2 border-line" />
                <Link
                  href="/login"
                  className={navLinkClass('/login')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign in
                </Link>
                <Link
                  href="/register"
                  className="px-3 py-2 rounded-md text-body-sm font-medium text-brand-600 hover:bg-brand-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      <footer className="bg-surface border-t border-line">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <p className="text-center text-body-sm text-muted">
            © 2024 Sloty. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
